import { useRef, useEffect, useState } from "react";
import { 
  Video, 
  Paintbrush, 
  MessageSquare, 
  Briefcase, 
  Camera, 
  Star,
  Building2,
  ArrowRight
} from "lucide-react";

const audiences = [
  {
    icon: Video,
    title: "Créateurs de contenu",
    description: "Visuels uniques pour vos réseaux sociaux en quelques secondes.",
    gradient: "from-rose-500 to-pink-500",
    users: "15K+"
  },
  {
    icon: Paintbrush,
    title: "Designers",
    description: "Accélérez votre workflow créatif avec l'IA comme assistant.",
    gradient: "from-orange-500 to-amber-500",
    users: "8K+"
  },
  {
    icon: MessageSquare,
    title: "Community Managers",
    description: "Créez du contenu engageant sans compétences graphiques.",
    gradient: "from-emerald-500 to-teal-500",
    users: "12K+"
  },
  {
    icon: Briefcase,
    title: "Entrepreneurs",
    description: "Visuels professionnels pour votre marque sans budget design.",
    gradient: "from-blue-500 to-cyan-500",
    users: "20K+"
  },
  {
    icon: Camera,
    title: "Photographes",
    description: "Retouches avancées et effets créatifs en un temps record.",
    gradient: "from-violet-500 to-purple-500",
    users: "5K+"
  },
  {
    icon: Star,
    title: "Influenceurs",
    description: "Démarquez-vous avec des visuels originaux et mémorables.",
    gradient: "from-pink-500 to-rose-500",
    users: "10K+"
  },
  {
    icon: Building2,
    title: "Agences",
    description: "Scalez votre production visuelle sans recruter.",
    gradient: "from-indigo-500 to-blue-500",
    users: "500+"
  },
];

export const AudienceSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-28 bg-card/30 relative overflow-hidden">
      {/* Animated gradient line */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-primary/50 to-transparent" />
      
      {/* Background orbs */}
      <div className="absolute top-1/2 left-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl -translate-y-1/2" />
      <div className="absolute top-1/2 right-0 w-96 h-96 bg-accent/5 rounded-full blur-3xl -translate-y-1/2" />
      
      <div className="container mx-auto px-6 relative z-10">
        <div className="text-center mb-16">
          <span 
            className={`inline-block text-sm font-medium text-primary mb-4 transition-all duration-700 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            }`}
          >
            Pour qui ?
          </span>
          <h2 
            className={`text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-6 font-display transition-all duration-700 delay-100 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            }`}
          >
            Floop s'adapte à{" "}
            <span className="bg-gradient-floop bg-clip-text text-transparent">tous les créatifs</span>
          </h2>
          <p 
            className={`text-lg text-muted-foreground max-w-2xl mx-auto transition-all duration-700 delay-200 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            }`}
          >
            Que vous soyez débutant ou professionnel, Floop a été conçu pour vous.
          </p>
        </div>

        {/* Hexagonal-inspired grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 max-w-6xl mx-auto">
          {audiences.map((audience, index) => (
            <div
              key={index}
              className={`group relative transition-all duration-700 ${
                isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
              } ${index === 6 ? "lg:col-start-2 lg:col-span-2" : ""}`}
              style={{ transitionDelay: `${300 + index * 100}ms` }}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              {/* Glow effect */}
              <div 
                className={`absolute -inset-0.5 bg-gradient-to-r ${audience.gradient} rounded-3xl blur opacity-0 group-hover:opacity-30 transition-opacity duration-500`}
              />
              
              {/* Card */}
              <div className="relative h-full bg-card/80 backdrop-blur-xl border border-border/50 rounded-3xl p-6 hover:border-primary/30 transition-all duration-500 overflow-hidden">
                {/* Background shimmer */}
                <div 
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  style={{
                    background: `linear-gradient(135deg, transparent 40%, hsl(var(--primary) / 0.05) 50%, transparent 60%)`,
                    backgroundSize: "200% 200%",
                    animation: hoveredIndex === index ? "shine 1.5s ease infinite" : "none"
                  }}
                />
                
                <div className="relative z-10 flex flex-col items-center text-center">
                  {/* Icon */}
                  <div className={`relative w-16 h-16 rounded-2xl flex items-center justify-center mb-4 transition-all duration-500 ${
                    hoveredIndex === index ? "scale-110" : ""
                  }`}>
                    <div className={`absolute inset-0 bg-gradient-to-r ${audience.gradient} rounded-2xl opacity-20 group-hover:opacity-100 transition-opacity duration-500`} />
                    <audience.icon className={`w-8 h-8 relative z-10 transition-colors duration-500 ${
                      hoveredIndex === index ? "text-white" : "text-primary"
                    }`} />
                  </div>
                  
                  {/* Content */}
                  <h3 className="text-lg font-bold text-foreground mb-2 group-hover:text-primary transition-colors">
                    {audience.title}
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
                    {audience.description}
                  </p>
                  
                  {/* Stats & CTA */}
                  <div className="flex items-center justify-between w-full pt-4 border-t border-border/50">
                    <div className="text-left">
                      <div className={`text-lg font-bold bg-gradient-to-r ${audience.gradient} bg-clip-text text-transparent`}>
                        {audience.users}
                      </div>
                      <div className="text-xs text-muted-foreground">utilisateurs</div>
                    </div>
                    <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center group-hover:bg-primary transition-colors duration-300">
                      <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-white transition-colors" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
